import { Injectable } from '@angular/core';
import { ModalService } from '../modal/modal.service';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MediaPermissionStore } from './media-permission.store';
import { MediaPermissionState } from '../../models/consent/media-permission.model';
import { MediaConsentModalComponent } from '../../../shared/components/consent/media-consent-modal/media-consent-modal.component';
import { CallType } from '../../models/group-call/group-call.model';

export type ConsentOutcome = 'proceed' | 'blocked' | 'cancelled';

export interface EnsureConsentOptions {
  recipientPerms?: MediaPermissionState;
  recipientLabel?: string;
  forceShow?: boolean;
  deviceFingerprint?: string;
  deviceMetadata?: string;
  action?: 'start' | 'join';
}

@Injectable({ providedIn: 'root' })
export class CallMediaConsentOrchestratorService {
  private activeModalRef?: NgbModalRef;

  constructor(
    private readonly modalService: ModalService,
    private readonly mediaPermissionStore: MediaPermissionStore
  ) {}

  async ensureConsentBeforeCall(
    callType: CallType | string | number,
    options: EnsureConsentOptions = {}
  ): Promise<ConsentOutcome> {
    await this.mediaPermissionStore.syncFromServer();
    const callerPerms = this.mediaPermissionStore.permissions();

    const forceShow = options.forceShow !== false;
    if (!forceShow) {
      return 'proceed';
    }

    return new Promise<ConsentOutcome>((resolve) => {
      let settled = false;
      const finish = (result: ConsentOutcome) => {
        if (settled) return;
        settled = true;
        resolve(result);
      };

      const modalRef = this.modalService.openComponentModal(
        MediaConsentModalComponent,
        { size: 'lg', centered: true, backdrop: 'static', keyboard: false }
      );
      this.activeModalRef = modalRef;
      const comp = modalRef.componentInstance as MediaConsentModalComponent;

      comp.visible = true;
      comp.callType = callType;
      comp.forceShow = forceShow;
      comp.initialAudioAllowed = callerPerms.audioAllowed;
      comp.initialVideoAllowed = callerPerms.videoAllowed;
      comp.applyInitialState(true);

      const deviceMetadata = options.deviceMetadata ?? navigator.userAgent;

      const onConfirmed = comp.confirmed.subscribe(async (result) => {
        onConfirmed.unsubscribe();
        onCancelled.unsubscribe();
        try {
          await this.mediaPermissionStore.updatePermissions(
            result.audioAllowed,
            result.videoAllowed,
            {
              deviceFingerprint: options.deviceFingerprint,
              deviceMetadata: deviceMetadata,
            }
          );
        } catch {}

        this.mediaPermissionStore.permissions();
        finish('proceed');
        modalRef.close();
        this.activeModalRef = undefined;
      });

      const onCancelled = comp.cancelled.subscribe(() => {
        onConfirmed.unsubscribe();
        onCancelled.unsubscribe();
        finish('cancelled');
        modalRef.close();
        this.activeModalRef = undefined;
      });

      modalRef.result
        .catch(() => finish('cancelled'))
        .finally(() => {
          onConfirmed.unsubscribe();
          onCancelled.unsubscribe();
          if (this.activeModalRef === modalRef) {
            this.activeModalRef = undefined;
          }
        });
    });
  }

  dismissActiveConsent(reason?: string): void {
    try {
      this.activeModalRef?.dismiss(reason ?? 'dismissed');
    } catch {}
    this.activeModalRef = undefined;
  }
}
